import { useEffect, useState } from 'react';
import { CubeTexture, CubeTextureLoader, Texture, TextureLoader } from 'three';
import THREELoader from '../common/three-loader';

const getLoaderByType = (type: string) => {
  let ClassType;
  let ClassTypeLoader;

  switch (type) {
    case 'cube': {
      ClassType = CubeTexture;
      ClassTypeLoader = CubeTextureLoader;
      break;
    }
    case 'multi':
    case 'pano':
    default: {
      ClassType = Texture;
      ClassTypeLoader = TextureLoader;
      break;
    }
  }

  return new THREELoader<typeof ClassType>(new ClassTypeLoader());
};

type TProps = {
  type: 'pano' | 'cube' | 'multi';
  source: string | string[];
};

const useSpacePhotoTexture = ({ source, type }: TProps) => {
  const [data, setData] = useState<{
    texture: Texture | Texture[];
    loaded: boolean;
  }>({
    texture: type === 'multi' ? (source as string[]).map(() => new Texture()) : new Texture(),
    loaded: false,
  });

  useEffect(() => {
    let loaderList: {
      loader: THREELoader<Texture>;
      promise: Promise<Texture>;
    }[] = [];

    const loader = getLoaderByType(type);

    setData(data => ({
      ...data,
      loaded: false,
    }));

    if (type === 'cube') {
      (async () => {
        try {
          const texture = ((await loader.load(source)) as unknown) as CubeTexture;

          texture.flipY = true;

          setData(data => ({
            ...data,
            texture,
            loaded: true,
          }));
        } catch (e) {
          setData(data => ({
            ...data,
            loaded: true,
          }));
          console.log('log: error: CubeTexture error: ', source, e);
        }
      })();
    } else if (type === 'multi') {
      (async () => {
        try {
          loaderList = Object.values(source).map(url => {
            const loader = new THREELoader<Texture>(new TextureLoader());

            return {
              loader,
              promise: loader.load(url),
            };
          });

          setData(data => ({
            ...data,
            loaded: false,
          }));

          const texture = await Promise.all(loaderList.map(itm => itm.promise));

          setData(data => ({
            ...data,
            texture,
            loaded: true,
          }));
        } catch (e) {
          setData(data => ({
            ...data,
            loaded: true,
          }));
          console.log('log: error: MultipleTextures error: ', source, e);
        }
      })();
    } else if (type === 'pano') {
      (async () => {
        try {
          const texture = ((await loader.load(source)) as unknown) as Texture;

          setData(data => ({
            ...data,
            texture,
            loaded: true,
          }));
        } catch (e) {
          setData(data => ({
            ...data,
            loaded: true,
          }));
          console.log('log: error: Texture error: ', source, e);
        }
      })();
    } else {
      setData(data => ({
        ...data,
        loaded: false,
      }));
    }

    return () => {
      loader.abort();
      loaderList.forEach(itm => itm.loader.abort());
    };
  }, [source, type]);

  return data;
};

export default useSpacePhotoTexture;
